import { useState } from "react";
import { Mail, Send, Loader2, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SOCIAL_ICONS } from "@/components/Glyphs";
import { SITE, SOCIALS } from "@/data/site";

const ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || "";

export default function Contact() {
  const [status, setStatus] = useState("idle");

  async function onSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = (data.get("name") || "").toString().trim();
    const email = (data.get("email") || "").toString().trim();
    const message = (data.get("message") || "").toString().trim();
    if (!name || !email || !message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }

    if (!ENDPOINT) {
      const subject = encodeURIComponent(`Warranty_Vault pro enquiry from ${name}`);
      const body = encodeURIComponent(`${message}\n\nFrom: ${name} <${email}>`);
      window.location.href = `mailto:${SITE.supportEmail}?subject=${subject}&body=${body}`;
      toast.success("Opening your email app to send the message…");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("send failed");
      form.reset();
      setStatus("idle");
      toast.success("Thanks — your message has been sent!");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please email us directly instead.");
    }
  }

  return (
    <section id="contact" className="relative scroll-mt-24 py-20 sm:py-28" data-testid="contact">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1fr]">
        <Reveal>
          <p className="eyebrow">Contact us</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Get in touch with the team
          </h2>
          <p className="mt-4 text-muted-foreground">
            Questions about plans, your account, or a bug? Send us a message and we'll get back to you as soon as possible.
          </p>

          <a
            href={`mailto:${SITE.supportEmail}`}
            data-testid="contact-support-email"
            className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-border bg-card/50 px-5 py-4 transition-colors hover:border-gold/40"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold">
              <Mail className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Email support</span>
              <span className="font-display font-semibold text-foreground">{SITE.supportEmail}</span>
            </span>
          </a>

          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-support"))}
            data-testid="help-support-btn"
            className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-5 py-4 text-left transition-colors hover:border-gold/50"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold/15 text-gold">
              <LifeBuoy className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-display font-semibold text-foreground">Help &amp; Support — chat with Vera</span>
              <span className="text-xs text-muted-foreground">Chat with Vera instantly; escalate to a human anytime</span>
            </span>
          </button>

          <div className="mt-8">
            <p className="eyebrow">Follow us</p>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="social-links">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a
                    key={s.href}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${s.icon}`}
                    className="group inline-flex items-center gap-2.5 rounded-full border border-border bg-card/50 px-4 py-2.5 text-sm font-medium transition-colors hover:border-gold/50 hover:text-gold"
                  >
                    <Icon className="h-[18px] w-[18px]" />
                    <span>{s.label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
            className="rounded-3xl border border-border bg-card/50 p-6 sm:p-8"
          >
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" name="name" autoComplete="name" placeholder="Your name" data-testid="contact-name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" autoComplete="email" placeholder="you@example.com" data-testid="contact-email" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" name="message" rows={5} placeholder="How can we help?" data-testid="contact-message" required />
              </div>
              <input name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="contact-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {status === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {status === "submitting" ? "Sending…" : "Send Message"}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                Prefer email? Reach us anytime at {SITE.supportEmail}
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
